/* @jsx h */
import { LemmonModal, useLocalization } from "@lemmonhq/uikit";
import { defineComponent, h, computed, ref, onMounted } from "vue";
import { useTrpc } from "@app/vue/providers/TrpcProvider";
import { instrumentation } from "@app/data/index";
import type { IntegratedPaymentWorkflowStatus } from "../../../workflows/types";
import { PaymentErrorCodes, type PaymentDetails } from "@lemmonhq/types";

interface WorkflowPaymentStatusModalProps {
  workflowId: string;
  workflowStatus?: {
    status: IntegratedPaymentWorkflowStatus;
    error?: string;
    payment?: PaymentDetails;
  };
  onClose: () => void;
  onCancel: () => void;
}

export const WorkflowPaymentStatusModal =
  defineComponent<WorkflowPaymentStatusModalProps>({
    name: "WorkflowPaymentStatusModal",
    props: ["workflowId", "workflowStatus", "onClose", "onCancel"],
    setup(props) {
      const trpc = useTrpc();
      const { t } = useLocalization();
      const NS = "checkout.payment_modal";
      // The server's generic outcomes and error codes are translated; a
      // provider's own message (e.g. Nakka's) is shown as sent.
      const serverMessageKeys: Record<string, string> = {
        "Payment cancelled": `${NS}.cancelled`,
        "Payment failed": `${NS}.failed`,
        [PaymentErrorCodes.PAYREXX_PAYMENT_ALREADY_INPROGRESS]: `${NS}.payrexx_payment_already_in_progress`,
      };
      const errorText = (error: string | undefined, fallbackKey: string) =>
        error
          ? serverMessageKeys[error]
            ? t(serverMessageKeys[error])
            : error
          : t(fallbackKey);
      const isCancelling = ref(false);
      // Cancel was sent to the provider. The modal stays open and keeps
      // polling until the payment really ends: a terminal can still finish
      // (or need its own cancel button) after we asked it to stop.
      const cancelRequested = ref(false);
      const cancelError = ref<string | null>(null);

      const statusMessage = computed(() => {
        if (!props.workflowStatus) {
          return t(`${NS}.initializing_workflow`);
        }

        if (cancelRequested.value && isProcessing.value) {
          return t(`${NS}.cancelling_payment`);
        }

        switch (props.workflowStatus.status) {
          case "initializing":
            return t(`${NS}.initializing`);
          case "waiting_for_provider":
            return t(`${NS}.connecting_provider`);
          case "provider_attached":
            return t(`${NS}.provider_connected`);
          case "waiting_for_confirmation":
            return t(`${NS}.waiting_confirmation`);
          case "payment_confirmed":
            return t(`${NS}.confirmed_processing`);
          case "processing_payment":
            return t(`${NS}.processing`);
          case "completed":
            return t(`${NS}.completed`);
          case "aborted":
            return errorText(props.workflowStatus.error, `${NS}.cancelled`);
          case "failed":
            return errorText(props.workflowStatus.error, `${NS}.failed`);
          case "timeout":
            return errorText(props.workflowStatus.error, `${NS}.timed_out`);
          default:
            return t(`${NS}.processing`);
        }
      });

      const isProcessing = computed(() => {
        if (!props.workflowStatus) return true;
        return (
          props.workflowStatus.status !== "completed" &&
          props.workflowStatus.status !== "aborted" &&
          props.workflowStatus.status !== "failed" &&
          props.workflowStatus.status !== "timeout"
        );
      });

      const isFailed = computed(() => {
        return (
          props.workflowStatus?.status === "aborted" ||
          props.workflowStatus?.status === "failed" ||
          props.workflowStatus?.status === "timeout"
        );
      });

      const isSuccess = computed(() => {
        return props.workflowStatus?.status === "completed";
      });

      const isCapturedFinalizing = computed(() => {
        return props.workflowStatus?.payment?.status === "confirmed";
      });

      const transactionMeta = computed(() => {
        const transaction = props.workflowStatus?.payment?.transaction;

        if (!transaction) {
          return null;
        }

        return {
          provider: transaction.provider,
          providerId: transaction.providerId,
          workflowType: transaction.workflow_type,
          workflowId: transaction.workflow_id,
          integrationId: transaction.integration_id,
        };
      });

      onMounted(() => {
        instrumentation.track("checkout_workflow_payment_modal_opened", {
          payment_workflow_id: props.workflowId,
          payment_id: props.workflowStatus?.payment?.id,
          payment_workflow_status: props.workflowStatus?.status,
        });
      });

      const handleClose = () => {
        instrumentation.track("checkout_workflow_payment_modal_closed", {
          payment_workflow_id: props.workflowId,
          payment_id: props.workflowStatus?.payment?.id,
          payment_workflow_status: props.workflowStatus?.status,
        });
        // After a cancel, closing hands control back to the checkout as a
        // cancelled payment; otherwise as an abandoned one.
        if (cancelRequested.value) {
          props.onCancel();
        } else {
          props.onClose();
        }
      };

      const handleCancel = async () => {
        if (isCancelling.value) {
          return;
        }

        isCancelling.value = true;
        cancelError.value = null;

        try {
          await trpc.integratedPaymentWorkflow.cancel.mutate({
            workflowId: props.workflowId,
          });
          instrumentation.track("checkout_integrated_payment_cancelled", {
            payment_workflow_id: props.workflowId,
            payment_id: props.workflowStatus?.payment?.id,
            payment_provider:
              props.workflowStatus?.payment?.transaction?.provider,
            payment_workflow_status: props.workflowStatus?.status,
          });
          cancelRequested.value = true;
        } catch (error) {
          console.error("Error cancelling workflow:", error);
          cancelError.value =
            error instanceof Error && error.message
              ? error.message
              : t(`${NS}.cancel_failed`);
        } finally {
          isCancelling.value = false;
        }
      };

      return () => (
        <LemmonModal
          title={t(`${NS}.title`)}
          onClose={() => handleClose()}
          hideClose={true}
          // While the payment is still running, a stray tap beside the modal
          // must not close it: closing stops polling, so a success that lands
          // a second later is never shown and cashiers re-enter the sale by
          // hand. It becomes dismissible once the Close button would show.
          disableCloseByClickingBackdrop={
            !(isFailed.value || isSuccess.value || isCapturedFinalizing.value)
          }
          renderButtons={() => (
            <div class="p-4 border-t ">
              <div class="flex justify-between gap-2">
                {isFailed.value ||
                isSuccess.value ||
                isCapturedFinalizing.value ? (
                  <button
                    onClick={() => handleClose()}
                    class="btn btn-neutral"
                    type="button"
                  >
                    {t(`${NS}.close`)}
                  </button>
                ) : (
                  <>
                    <button
                      onClick={handleCancel}
                      class={`btn btn-error ${
                        isCancelling.value || cancelRequested.value
                          ? "btn-disabled"
                          : ""
                      }`}
                      type="button"
                      disabled={isCancelling.value || cancelRequested.value}
                    >
                      {isCancelling.value || cancelRequested.value ? (
                        <span class="inline-flex items-center gap-2">
                          <span class="loading loading-spinner loading-xs"></span>
                          {t(`${NS}.cancelling`)}
                        </span>
                      ) : (
                        t(`${NS}.cancel`)
                      )}
                    </button>
                    <div class="flex-1 flex justify-end items-center text-warning text-xs">
                      {!cancelRequested.value
                        ? t(`${NS}.cancel_on_terminal_hint`)
                        : transactionMeta.value?.provider === "payrexx_softpos"
                          ? t(`${NS}.checking_softpos`)
                          : t(`${NS}.cancel_on_terminal_after`)}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        >
          <div class="relative p-6 pt-8 flex flex-col items-center text-center">
            {transactionMeta.value?.provider ? (
              <div class="absolute left-4 top-3 text-[10px] uppercase tracking-[0.18em] ">
                {transactionMeta.value.provider}
              </div>
            ) : null}

            {transactionMeta.value?.workflowId ? (
              <div class="absolute right-4 top-3 max-w-[40%] truncate text-[10px] font-mono ">
                {transactionMeta.value.workflowId}
              </div>
            ) : null}

            <div class="py-2 flex justify-center">
              {isProcessing.value ? (
                <div class="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
              ) : isSuccess.value ? (
                <div class="text-green-500">
                  <span class="material-symbols-outlined text-6xl">
                    check_circle
                  </span>
                </div>
              ) : (
                <div class="text-red-500">
                  <span class="material-symbols-outlined text-6xl">error</span>
                </div>
              )}
            </div>

            <div class="space-y-1 text-accent">
              <div class="text-xl font-medium">{statusMessage.value}</div>
              {cancelError.value ? (
                <div class="text-sm text-error">{cancelError.value}</div>
              ) : null}
            </div>

            {transactionMeta.value?.workflowType ||
            transactionMeta.value?.workflowId ||
            transactionMeta.value?.integrationId ? (
              <div class="mt-5 flex w-full flex-wrap justify-center gap-x-4 gap-y-1 text-[10px] ">
                {transactionMeta.value?.workflowType ? (
                  <span class="font-mono">
                    wf: {transactionMeta.value.workflowType}
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>
        </LemmonModal>
      );
    },
  });
